import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {TripbookRepository} from '../repositories/TripbookRepository.ts'
import {AuthenticationRepository} from '../repositories/AuthenticationRepository.ts'
import {AssignInvitationCodeGroupPayload, SyncEbookOption, UpdateInvitationCodePayload} from '../domain/entities/InvitationCode.ts'

const tripbookRepository = new TripbookRepository(new AuthenticationRepository())

export const INVITATION_CODES_QUERY_KEY = 'invitation-codes' as const

export function useInvitationCodesByAgency(agencyId: string) {
    const query = useQuery({
        queryKey: [INVITATION_CODES_QUERY_KEY, agencyId],
        queryFn: () => tripbookRepository.getInvitationCodesByAgency(agencyId),
        enabled: !!agencyId,
    })

    return {
        invitationCodes: query.data ?? [],
        isLoadingInvitationCodes: query.isLoading,
    }
}

export function useUpdateInvitationCode(agencyId: string) {
    const queryClient = useQueryClient()

    const mutation = useMutation({
        mutationFn: (payload: UpdateInvitationCodePayload & SyncEbookOption) => tripbookRepository.updateInvitationCode(payload),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: [INVITATION_CODES_QUERY_KEY, agencyId]})
            queryClient.invalidateQueries({queryKey: ['b2b-pack-detail', agencyId]})
            queryClient.invalidateQueries({queryKey: ['b2b-agency-activity', agencyId]})
        },
    })

    return {
        updateInvitationCode: mutation.mutateAsync,
        isUpdatingInvitationCode: mutation.isPending,
    }
}

export function useAssignInvitationCodeGroup(agencyId: string) {
    const queryClient = useQueryClient()

    const mutation = useMutation({
        mutationFn: (payload: AssignInvitationCodeGroupPayload) => tripbookRepository.assignInvitationCodeGroup(payload),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: [INVITATION_CODES_QUERY_KEY, agencyId]})
            queryClient.invalidateQueries({queryKey: ['b2b-pack-detail', agencyId]})
            queryClient.invalidateQueries({queryKey: ['b2b-agency-activity', agencyId]})
        },
    })

    return {
        assignInvitationCodeGroup: mutation.mutateAsync,
        isAssigningInvitationCodeGroup: mutation.isPending,
    }
}

export function useCancelInvitationCode(agencyId: string) {
    const queryClient = useQueryClient()

    const mutation = useMutation({
        mutationFn: (invitationCodeId: string) => tripbookRepository.cancelInvitationCode(invitationCodeId),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: [INVITATION_CODES_QUERY_KEY, agencyId]})
            queryClient.invalidateQueries({queryKey: ['b2b-pack-detail', agencyId]})
            queryClient.invalidateQueries({queryKey: ['b2b-agency-activity', agencyId]})
        },
    })

    return {
        cancelInvitationCode: mutation.mutateAsync,
        isCancelingInvitationCode: mutation.isPending,
    }
}
